import { type ActionEvent, Controller } from "@hotwired/stimulus"

import DisclosureItemController from "@/controllers/disclosure_item_controller"
import ModalController from "@/controllers/modal_controller"
import * as Ga4 from "@/helpers/ga4_helper"

const EXPERIENCES = Object.freeze({
  SPA_DAY: "spa_days",
  SPA_BREAK: "spa_breaks"
} as const)
type Experience = (typeof EXPERIENCES)[keyof typeof EXPERIENCES]

export default class extends Controller {
  static outlets = ["booking-modal", "disclosure-item", "package-voucher-modal"]
  declare readonly bookingModalOutlets: ModalController[]
  declare readonly disclosureItemOutlets: DisclosureItemController[]
  declare readonly packageVoucherModalOutlets: ModalController[]

  static targets = ["experienceGroup", "package"]
  declare readonly experienceGroupTargets: HTMLElement[]
  declare readonly packageTargets: HTMLElement[]

  static values = {
    selectedExperience: String
  }
  declare selectedExperienceValue: Experience

  initialize(): void {
    this.toggleStickyStatusBarOnIntersection()
  }

  selectedExperienceValueChanged(_current: Experience, previous: Experience): void {
    this.experienceGroupTargets.forEach(this.toggleExperienceGroupVisibility.bind(this))

    // Value change callbacks are invoked on init which is fine in most cases, but we don't want
    // to fire GA4 event until radio has actually been selected. The previous value will be
    // `undefined` on init, so check that before firing event.
    if (previous) {
      this.ga4ViewItemList()
    }
  }

  initPackageDetailsCtas(): void {
    // Disclosure item CTAs are shown/hidden when that controller connects, but if the
    // corresponding elements are hidden at the time then the CTAs will never show, even if
    // they should.
    // Packages for non-selected radios are hidden on page load, so re-init the CTAs
    // when those packages become visible after a radio button is selected.
    this.disclosureItemOutlets.forEach((outlet) => {
      if (this.selectedExperienceGroup.contains(outlet.element)) {
        outlet.toggleCtaVisibility()
      }
    })
  }

  bookPackage({ params: { ecommerceItem, index, packageId } }: ActionEvent): void {
    this.bookingModalOutletForPackage(packageId)?.open()

    Ga4.selectItemEvent({
      ...ecommerceItem,
      index,
      item_list_name: this.experienceTitle
    })
  }

  buyMonetaryVoucher(): void {
    Ga4.selectItemEvent({
      item_category: "Monetary Voucher",
      item_list_name: this.experienceTitle,
      item_name: "£100",
      price: 100,
      quantity: 1
    })

    Ga4.virtualPageViewEvent("Amount Selection", "/voucher_checkout/amount-selection")

    this.dispatch("buyMonetaryVoucher")
  }

  buyPackageVoucher({ params: { ecommerceItem, index, packageId } }: ActionEvent): void {
    this.voucherModalOutletForPackage(packageId)?.open()

    Ga4.selectItemEvent({
      ...ecommerceItem,
      index,
      item_category: "Package Voucher",
      item_list_name: this.experienceTitle
    })
  }

  selectExperience({ currentTarget, params }: ActionEvent): void {
    this.selectedExperienceValue = (currentTarget as HTMLInputElement).value as Experience
    this.updateStickyViewPackageButtonText(params.viewPackagesText)

    Ga4.selectContentEvent("Experiences", this.experienceTitle)
  }

  private bookingModalOutletForPackage(packageId: string): ModalController | undefined {
    return this.bookingModalOutlets.find(
      (modal) => modal.containerId === `booking-modal-${packageId}`
    )
  }

  private get experienceTitle(): string {
    return this.isSpaBreaksSelected ? "Spa breaks" : "Spa days"
  }

  private ga4ViewItemList(): void {
    Ga4.viewItemListEvent(
      this.experienceTitle,
      this.packageTargets.flatMap((pkg, index) =>
        this.selectedExperienceGroup.contains(pkg)
          ? [{ ...Ga4.tryParseJSON(pkg.dataset.ecommerceItem!), index }]
          : []
      ) as Ga4.EcommerceItem[]
    )
  }

  private get isSpaBreaksSelected(): boolean {
    return this.selectedExperienceValue !== EXPERIENCES.SPA_DAY
  }

  private toggleStickyStatusBarOnIntersection(): void {
    const packagesSection = document.getElementById("packages")
    const viewPackagesButton = document.getElementById("js-view-packages-button")!
    let packagesViewed = false

    document.documentElement.style.setProperty(
      "--view-packages-height",
      `${viewPackagesButton.getBoundingClientRect().height}px`
    )

    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach(({ isIntersecting }) => {
          // Show/hide status bar
          viewPackagesButton?.classList.toggle("is-hidden", isIntersecting)

          // Fire GA4 event if first time seeing packages
          if (isIntersecting && !packagesViewed) {
            this.ga4ViewItemList()
            packagesViewed = true
          }
        })
      },
      { root: null, rootMargin: "-25% 0px", threshold: 0 }
    )

    if (packagesSection) {
      observer.observe(packagesSection)
    }
  }

  private get selectedExperienceGroup(): HTMLElement {
    return this.experienceGroupTargets.find(
      (group) => group.dataset.experience === this.selectedExperienceValue
    )!
  }

  private toggleExperienceGroupVisibility(group: HTMLElement): void {
    if (this.isSpaBreaksSelected) {
      group.hidden = group.dataset.experience === EXPERIENCES.SPA_DAY
    } else {
      group.hidden = group.dataset.experience !== EXPERIENCES.SPA_DAY
    }
  }

  private voucherModalOutletForPackage(packageId: string): ModalController | undefined {
    return this.packageVoucherModalOutlets.find(
      (modal) => modal.containerId === `package-voucher-modal-${packageId}`
    )
  }

  private updateStickyViewPackageButtonText(text: string): void {
    const statusBar = document.getElementById("js-view-packages-button")
    const button = statusBar?.querySelector("a")

    if (button) {
      button.textContent = text
    }
  }
}
